/**
 * Placing a catalog asset. Finding one is a server's (`search_catalog` in
 * `trace-viewer/cms`); putting it in the scene is the editor's, because only
 * the host knows how its editor imports (rigs, pages, the open states context)
 * and records the step. The tool reads the input and says where it landed.
 */

import type { SceneObject } from '../../core/SceneObject';
import { readOnlyRefusal } from '../authoring/referenceEdit';
import { PLAY_MODE_REFUSAL } from '../authoring/types';
import { VEC3, defineTool, fail, readTransformArgs, refusalText, resolveRef, whereText } from './input';
import type { SceneTool } from './types';

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const importAssetTool = defineTool({
    name: 'import_asset',
    verb: 'Adding',
    edits: true,
    additive: true,
    inputSchema: {
        type: 'object',
        properties: {
            asset_id: { type: 'string' }, url: { type: 'string' }, name: { type: 'string' }, parent: { type: 'string' },
            position: VEC3, rotation: VEC3, scale: VEC3,
        },
    },
    requires: ['importAsset'],
    async run(input, host) {
        // Every other edit is refused in play mode by its authoring operation; placement is the host's, so here.
        if (host.scene.isPlayMode()) return fail(`Error: ${refusalText(host, PLAY_MODE_REFUSAL)}`);
        if (input.asset_id && input.url) return fail('Error: Give asset_id or url, not both.');
        const assetId = typeof input.asset_id === 'string' && input.asset_id ? input.asset_id : undefined;
        const url = typeof input.url === 'string' && input.url ? input.url : undefined;
        if (!assetId && !url) return fail('Error: Give asset_id (from search_catalog) or url.');
        if (assetId && !GUID.test(assetId)) return fail(`Error: "${assetId}" is not an asset id; ids come from search_catalog.`);
        if (url && !/^https?:\/\//.test(url)) return fail('Error: url must be http(s).');
        const pose = readTransformArgs(input);
        if ('error' in pose) return fail(pose.error);
        // No parent named: the host's own default place (Studio nests in the open page or states context).
        let parent: SceneObject | null = null;
        if (typeof input.parent === 'string' && input.parent) {
            parent = resolveRef(host, input.parent);
            if (!parent) return fail(`Error: Parent object "${input.parent}" not found.`);
            const readOnly = readOnlyRefusal(parent, host.ports());
            if (readOnly) return fail(`Error: ${refusalText(host, readOnly)}`);
        }
        const name = typeof input.name === 'string' && input.name.trim() ? input.name.trim() : undefined;
        // `requires` has checked the host can place.
        const placed = await host.importAsset!({ assetId, url, name, parent, pose });
        if (typeof placed === 'string') return fail(`Error: ${placed}`);
        host.select?.(placed);
        host.changed?.();
        return { text: `Placed "${placed.name}".${whereText(placed)}` };
    },
});

export const ASSET_TOOLS: readonly SceneTool[] = [importAssetTool];
